import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import api from 'api/api'
import { buildNotificationsFilter } from 'components/filters/streamFilterUtils'
import useStreamFilter, { Filter, pathFromFilter } from 'components/filters/useStreamFilters'
import Search from 'components/panels/Search'
import SearchHeader from 'components/panels/SearchHeader'
import UserProfile from 'components/panels/UserProfile'
import usePanelContext from 'refactor/hooks/usePanelContext'
import { isMobile } from 'lib/device'
import styles from 'components/panels/panel.module.scss'

function initialNotificationTab(filter: Filter) {
    const savedTab = localStorage.getItem('notificationTab')
    if (savedTab && ['all', 'messages', 'upvalues', 'system'].includes(savedTab)) {
        return savedTab
    }
    return filter?.upvalue_notis
        ? 'upvalues'
        : filter?.system_notis
        ? 'system'
        : filter?.message_notis
        ? 'messages'
        : 'all'
}

export default function PanelStreamHeader({ filter }: { filter: Filter }) {
    const { panel, setIsRefreshing } = usePanelContext()
    const [activeNotificationTab, setActiveNotificationTab] = useState(() =>
        initialNotificationTab(filter),
    )
    const { selectNotifications } = useStreamFilter(panel.panelId, { append: false })
    const isUserProfile = Boolean(filter?.user || filter?.user_ordinals || filter?.user_music)
    const isSearch = Boolean(filter?.search)
    const isThinStream = Boolean(filter?.starred)

    const handleSetIsRefreshing = (value: boolean) => {
        setIsRefreshing?.(value)
    }

    const handleNotificationTabChange = (tab: string) => {
        setActiveNotificationTab(tab)
        localStorage.setItem('notificationTab', tab)
        let notificationTabFilter: Partial<Filter> = {}

        if (tab === 'messages') {
            notificationTabFilter = { message_notis: true }
        } else if (tab === 'upvalues') {
            notificationTabFilter = { upvalue_notis: true }
        } else if (tab === 'system') {
            notificationTabFilter = { system_notis: true }
        }

        const newFilter = buildNotificationsFilter(filter, notificationTabFilter)
        selectNotifications(notificationTabFilter)
        history.pushState({ filter: newFilter }, '', pathFromFilter(newFilter))
        api.updateUserSpaceOptions({ filter: newFilter })
    }

    useEffect(() => {
        if (!filter?.notifications) return
        const hasTabFlag = filter?.message_notis || filter?.upvalue_notis || filter?.system_notis
        if (!hasTabFlag && activeNotificationTab !== 'all') {
            handleNotificationTabChange(activeNotificationTab)
        }
    }, [
        filter?.notifications,
        filter?.message_notis,
        filter?.upvalue_notis,
        filter?.system_notis,
        activeNotificationTab,
    ])

    return (
        <div
            data-testid="stream-header-shell"
            className={cn(
                isThinStream ? styles.thinSearchHeaderContainer : styles.searchHeaderContainer,
                isMobile && !isThinStream && 'full-bleed-quests',
            )}
        >
            {isSearch ? (
                <Search filter={filter} setIsRefreshing={handleSetIsRefreshing} quest={null} />
            ) : isUserProfile ? (
                <UserProfile
                    filter={filter}
                    setIsRefreshing={handleSetIsRefreshing}
                    quest={null}
                    panel={panel}
                />
            ) : (
                <SearchHeader
                    filter={filter}
                    setIsRefreshing={handleSetIsRefreshing}
                    quest={null}
                    activeNotificationTab={activeNotificationTab}
                    onNotificationTabChange={handleNotificationTabChange}
                />
            )}
        </div>
    )
}
